import * as React from "react";

interface FunnelErrorFallbackProps {
  error: unknown;
  resetError: () => void;
}

/** DOM-mutation conflicts (translation plugins, extensions) leave React's tree out of sync with the DOM. */
const isDomMutationError = (error: unknown) =>
  error instanceof Error && error.name === "NotFoundError" && /removeChild|insertBefore/.test(error.message);

const MESSAGES: Record<string, { text: string; button: string }> = {
  de: { text: "Etwas ist schiefgelaufen.", button: "Seite neu laden" },
  fr: { text: "Une erreur s'est produite.", button: "Recharger la page" },
  it: { text: "Qualcosa è andato storto.", button: "Ricarica la pagina" },
  en: { text: "Something went wrong.", button: "Reload page" },
};

/**
 * Replaces Sentry's default blank fallback. A DOM-mutation conflict is recovered from by remounting the tree once;
 * everything else (or a repeated failure) shows a reload prompt instead of a white page. Deliberately uses no
 * i18n/context, since those providers sit inside the boundary.
 */
export const FunnelErrorFallback: React.FC<FunnelErrorFallbackProps> = ({ error, resetError }) => {
  const retried = React.useRef(false);
  const recoverable = isDomMutationError(error) && !retried.current;

  React.useEffect(() => {
    if (recoverable) {
      retried.current = true;
      resetError();
    }
  }, [recoverable, resetError]);

  if (recoverable) return <></>;

  const lang = (typeof navigator !== "undefined" ? navigator.language : "de").slice(0, 2);
  const msg = MESSAGES[lang] ?? MESSAGES.de;
  return (
    <div role="alert" className="p-6 text-center space-y-3">
      <p>{msg.text}</p>
      <button type="button" className="underline" onClick={() => window.location.reload()}>
        {msg.button}
      </button>
    </div>
  );
};
